// Prod is identified by the `prod.` subdomain prefix.
const isProdEnvironment = (): boolean => {
  return window.location.hostname.startsWith('prod.');
};

/**
 * Deployment stages this UI is published to, as they appear in our own
 * hostname: `<stage>.clinicalcopilot.pharmacy.amazon.dev`. Personal dev
 * stacks live under `<alias>.dev.clinicalcopilot.pharmacy.amazon.dev`, and
 * local development runs on `localhost`; both resolve to `dev`.
 */
export type Stage = 'prod' | 'gamma' | 'beta' | 'alpha' | 'dev';

/**
 * Our own stage, read from the hostname at call time (never cached — tests
 * and the dev server both rewrite `window.location`).
 *
 * Anything unrecognised falls through to `dev`, which is the conservative
 * choice for everything *except* origin allowlisting: a `dev` stage has no
 * configured host origins, and an empty allowlist is treated as open in
 * pre-prod. That is safe only because `isProdEnvironment()` is checked
 * independently there — see `hostBridge.isOriginAllowed`.
 */
export const resolveStage = (): Stage => {
  const { hostname } = window.location;
  if (hostname.startsWith('prod.')) return 'prod';
  if (hostname.startsWith('gamma.')) return 'gamma';
  if (hostname.startsWith('beta.')) return 'beta';
  if (hostname.startsWith('alpha.')) return 'alpha';
  return 'dev';
};

export const isPreProdEnvironment = (): boolean => {
  return !isProdEnvironment();
};

// Feature access is gated purely on environment: disabled in prod,
// enabled in every other environment. The permissions list is kept
// in the signature so call sites remain unchanged, but is not used
// at this time.
export const hasFeatureAccess = (_userPermissions: string[]): boolean => {
  return !isProdEnvironment();
};
